import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Text } from '../../../../components';
import { PICK_TIP_MAX_W_PX, PickBody, PickBodyTip } from './PickLine.styles';

/** Room between the line and its tooltip, and the tooltip's least room from the screen's edges. */
const TIP_GAP_PX = 8;
const EDGE_PX = 16;
/** Less room than this over the line: the tooltip goes under it. */
const ROOM_ABOVE_PX = 120;

type TipSpot = { left: number; top: number; below: boolean };

type LineRef = React.RefObject<HTMLParagraphElement>;

type TipHandlers = Required<
  Pick<
    React.HTMLAttributes<HTMLParagraphElement>,
    'onPointerEnter' | 'onPointerLeave' | 'onPointerDown' | 'onClick' | 'onFocus' | 'onBlur'
  >
>;

/** Over the line, or under it near the screen's top; kept off the screen's edges. */
const tipSpotOf = (box: DOMRect): TipSpot => {
  const below = box.top < ROOM_ABOVE_PX;
  return {
    left: Math.max(EDGE_PX, Math.min(box.left, window.innerWidth - PICK_TIP_MAX_W_PX - EDGE_PX)),
    top: below ? box.bottom + TIP_GAP_PX : box.top - TIP_GAP_PX,
    below,
  };
};

/** Whether the line is cut: measured again whenever its box changes, and when its words do (another language). */
const useLineCut = (ref: LineRef, words: string): boolean => {
  const { i18n } = useTranslation();
  const [cut, setCut] = React.useState(false);
  React.useLayoutEffect(() => {
    const line = ref.current;
    if (!line) return undefined;
    const measure = (): void => setCut(line.scrollHeight > line.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(line);
    return () => observer.disconnect();
  }, [ref, words, i18n.language]);
  return cut;
};

/** Open, the tooltip closes as the page scrolls (it is fixed there), on a tap anywhere else and on Escape. */
const useTipDismiss = (isOpen: boolean, ref: LineRef, close: () => void): void => {
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onOutside = (event: PointerEvent): void => {
      const line = ref.current;
      if (!line || !(event.target instanceof Node) || !line.contains(event.target)) close();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onOutside);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', onOutside);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
    };
  }, [isOpen, ref, close]);
};

/**
 * The tooltip of a cut line: while a mouse rests on it, on a tap (touch screens; a tap anywhere else closes it), or on
 * keyboard focus.
 */
const usePickTip = (ref: LineRef, cut: boolean): { tip: TipSpot | null; handlers: TipHandlers } => {
  const [tip, setTip] = React.useState<TipSpot | null>(null);
  const pointerType = React.useRef('mouse');
  const open = React.useCallback(() => {
    const line = ref.current;
    if (cut && line) setTip(tipSpotOf(line.getBoundingClientRect()));
  }, [ref, cut]);
  const close = React.useCallback(() => setTip(null), []);
  useTipDismiss(tip !== null, ref, close);

  const hover = (event: React.PointerEvent<HTMLParagraphElement>, entering: boolean): void => {
    if (event.pointerType !== 'mouse') return;
    if (entering) open();
    else close();
  };
  const handlers: TipHandlers = {
    onPointerEnter: (event) => hover(event, true),
    onPointerLeave: (event) => hover(event, false),
    onPointerDown: (event) => {
      pointerType.current = event.pointerType;
    },
    // A tap toggles it (a mouse already opened it by resting there).
    onClick: () => {
      if (pointerType.current === 'mouse') return;
      if (tip) close();
      else open();
    },
    // From the keyboard only: a tap's focus would open it just before the tap toggles it shut.
    onFocus: (event) => {
      if (event.currentTarget.matches(':focus-visible')) open();
    },
    onBlur: close,
  };
  return { tip, handlers };
};

export type PickLineProps = {
  /** The line, an English key, rendered through Text here and in the tooltip. */
  text: string;
  /** Its placeholders' values. */
  placeholders?: Record<string, string | number>;
  /** Off: a cut line takes no keyboard focus (it sits in a card that is itself the control, so nothing nests in it). */
  focusable?: boolean;
};

/**
 * A pick's line, three lines at most (the prototype's PickLine): a longer one is cut with an ellipsis, and its whole
 * text shows in a small tooltip while the pointer rests on it, on a tap, or on keyboard focus. The text is whole in the
 * page either way, so a screen reader reads it all (the tooltip is hidden from it).
 */
export const PickLine = ({ text, placeholders, focusable = true }: PickLineProps): React.ReactElement => {
  const ref = React.useRef<HTMLParagraphElement>(null);
  const cut = useLineCut(ref, `${text}|${JSON.stringify(placeholders ?? {})}`);
  const { tip, handlers } = usePickTip(ref, cut);
  return (
    <>
      <PickBody ref={ref} $cut={cut} tabIndex={cut && focusable ? 0 : undefined} {...handlers}>
        <Text text={text} placeholders={placeholders} />
      </PickBody>
      {tip
        ? createPortal(
            <PickBodyTip aria-hidden="true" $below={tip.below} style={{ left: tip.left, top: tip.top }}>
              <Text text={text} placeholders={placeholders} />
            </PickBodyTip>,
            document.body,
          )
        : null}
    </>
  );
};

export default PickLine;
